import React, { Fragment, ReactNode } from 'react';
import {
    Grid,
    Paper,
    Skeleton,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiAlertCircle } from '@mdi/js';

import { ValueReferences } from '../../utils/enums';

const classesPrefix = 'kpiCard';

const classes = {
    cardContainer: `${classesPrefix}-cardContainer`,
    card: `${classesPrefix}-card`,
    value: `${classesPrefix}-value`,
    unit: `${classesPrefix}-unit`,
    valueContainer: `${classesPrefix}-valueContainer`,
    error: `${classesPrefix}-error`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`&.${classes.cardContainer}`]: {
            display: 'flex',
            alignItems: 'stretch',
            flexShrink: 1,
            minWidth: 140,
        },
        [`& .${classes.card}`]: {
            padding: theme.spacing(1),
            flexGrow: 1,
            minHeight: theme.spacing(7),
            position: 'relative',
        },
        [`& .${classes.value}`]: {
            flexGrow: 1,
        },
        [`& .${classes.unit}`]: {
            color: theme.palette.grey[500],
            lineHeight: 'initial',
        },
        [`& .${classes.valueContainer}`]: {
            display: 'flex',
            alignItems: 'flex-end',
        },
        [`& .${classes.error}`]: {
            position: 'absolute',
            top: 0,
            right: 0,
            margin: theme.spacing(0.5),
            color: theme.palette.error.main,
        },
    };
});

const KPICard = ({
    loading,
    title,
    stat,
    prefix,
    unit,
    error,
    children,
}: {
    loading: boolean;
    title: string;
    stat: string | number | null;
    /** prefix shown before the value displayed (e.g. $) */
    prefix?: string;
    /** unit of measure, displayed after the value (e.g. hr) */
    unit?: string; // 'km' | 'mi' | 'usd' | '%' | 'hr',
    /** error if the stat cannot be calculated */
    error?: string | null;
    children?: ReactNode;
}): React.JSX.Element => {
    return (
        <StyledGrid item xs={12} sm={6} lg className={classes.cardContainer}>
            <Paper elevation={1} className={classes.card} square>
                <Grid
                    container
                    direction='column'
                    sx={{
                        alignItems: 'stretch',
                    }}
                >
                    <Grid item>
                        <Typography variant='subtitle2'>
                            {loading ? <Skeleton /> : title}
                        </Typography>
                    </Grid>
                    <Grid item className={classes.valueContainer}>
                        <Fragment>
                            <Typography
                                variant='h5'
                                className={classes.value}
                            >
                                {
                                    loading ?
                                        (
                                            <Skeleton />
                                        ) :
                                        (
                                            (prefix || '') +
                                            (stat === null || stat === undefined ? ValueReferences.NA : stat)
                                        )
                                }
                            </Typography>
                            {
                                unit && !loading &&
                                <Typography
                                    variant='overline'
                                    className={classes.unit}
                                >
                                    {unit}
                                </Typography>
                            }
                        </Fragment>
                    </Grid>
                    <Grid item>{children}</Grid>
                </Grid>
                {
                    error &&
                    <Tooltip title={error}>
                        <Icon
                            className={classes.error}
                            path={mdiAlertCircle}
                            size={1}
                        />
                    </Tooltip>
                }
            </Paper>
        </StyledGrid>
    );
};

export default KPICard;
